import { Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import { PurchaseOrderFacade } from './purchase-order.facade';
import { PublicSignSession, PurchaseOrder } from '@models/resources/purchase-order.model';

/**
 * Dummy-Variante der Customer-Kaufauftrag-Actions (Sprint 9, ADR-011).
 *
 * Simuliert eine Erstkauf-Order im `sent`-Zustand und schiebt sie durch
 * die Kunden-Schritte (Ident-Wahl → Signatur → signiert), damit die
 * Auftragsseite im dummy-Modus klickbar bleibt.
 */
@Injectable()
export class DummyPurchaseOrderFacade implements PurchaseOrderFacade {
  private order: PurchaseOrder = this.seed(9001);

  get(id: number): Observable<PurchaseOrder> {
    if (this.order.id !== id) {
      this.order = this.seed(id);
    }
    return of(this.order).pipe(delay(120));
  }

  chooseIdentityMethod(_id: number, method: 'video' | 'post'): Observable<PurchaseOrder> {
    this.order = {
      ...this.order,
      identity_method: method,
      current_step: method === 'post' ? 'identity_pending_post' : 'identity_pending_video',
      ident_redirect_url: method === 'video' ? 'https://example.test/video-ident' : null,
    };
    return of(this.order).pipe(delay(120));
  }

  uploadIdDocuments(_id: number, _front: File, _back: File): Observable<PurchaseOrder> {
    this.order = { ...this.order, current_step: 'signature' };
    return of(this.order).pipe(delay(150));
  }

  startSignature(_id: number): Observable<PurchaseOrder> {
    this.order = {
      ...this.order,
      signature_provider: 'inhouse',
      signature_url: `/sign/dummy-token-${this.order.id}`,
      sign_token: `dummy-token-${this.order.id}`,
      current_step: 'signature',
    };
    return of(this.order).pipe(delay(120));
  }

  submitSignature(_id: number, _png: Blob): Observable<PurchaseOrder> {
    this.order = {
      ...this.order,
      status: 'signed',
      signed_at: new Date().toISOString(),
      current_step: 'signed',
    };
    return of(this.order).pipe(delay(150));
  }

  requestSignLink(_id: number): Observable<PurchaseOrder> {
    // Frischer „Token" — hält den QR im FE synchron.
    this.order = {
      ...this.order,
      signature_url: `/sign/dummy-token-${this.order.id}-mail`,
      sign_token: `dummy-token-${this.order.id}-mail`,
    };
    return of(this.order).pipe(delay(120));
  }

  couponUrl(_id: number): string {
    return `/assets/dummy/postident-coupon.pdf`;
  }

  pdfUrl(_id: number): string {
    return `/assets/dummy/kaufauftrag.pdf`;
  }

  getPublicSignSession(_token: string): Observable<PublicSignSession> {
    return of({
      id: this.order.id,
      metal: this.order.metal,
      amount_eur: this.order.amount_eur,
      status: this.order.status,
      current_step: this.order.current_step,
      signed_at: this.order.signed_at,
    }).pipe(delay(120));
  }

  publicSignPdf(_token: string): Observable<Blob> {
    return of(new Blob(['%PDF-1.4 dummy'], { type: 'application/pdf' })).pipe(delay(120));
  }

  submitPublicSignature(_token: string, _png: Blob): Observable<PublicSignSession> {
    this.order = {
      ...this.order,
      status: 'signed',
      signed_at: new Date().toISOString(),
      current_step: 'signed',
    };
    return of({
      id: this.order.id,
      metal: this.order.metal,
      amount_eur: this.order.amount_eur,
      status: this.order.status,
      current_step: this.order.current_step,
      signed_at: this.order.signed_at,
    }).pipe(delay(150));
  }

  private seed(id: number): PurchaseOrder {
    const now = new Date().toISOString();
    return {
      id,
      transaction_request_id: 1,
      metal: { id: 1, symbol: 'Au', name: 'Gold' },
      amount_eur: 10000,
      status: 'sent',
      identity_method: null,
      revision: 1,
      current_step: 'identity_choice',
      sent_at: now,
      signed_at: null,
      confirmed_at: null,
      returned_at: null,
      return_comment: null,
      signature_provider: null,
      ident_redirect_url: null,
      signature_url: null,
      sign_token: null,
      documents: [],
      created_at: now,
      updated_at: now,
    };
  }
}
